import { DevTool } from "@hookform/devtools";
import {
  Badge,
  BankAccountResolver,
  ModalContainer,
  SectionHeader,
  SwitchAppButton,
} from "../../../components";
import {
  HookFormSelect,
  HookFormToggle,
  SubmitButton,
  TextArea,
  TextInput,
} from "../../../hook-form";
import { filterSupportedCountriesFromList } from "../../../utils";
import { IBankAccountFormViewProps } from "./BankAccountForm";

export const View = ({
  isModalOpen,
  closeModal,
  isLoadingSupportedCountries,
  swAppSupportedCountries,
  isLoadingSupportedCurrencies,
  swAppSupportedCurrencies,
  isLoadingBankList,
  bankList,

  bankAccountTagsFieldArray,
  appendNewBankAccountTag,
  removeBankAccountTagByIndex,

  isBankAccountResolved,
  setBankAccountResolved,

  formValues,
  onToggleSetPrimary,
  control,
  errors,
  handleSubmit,
  register,
}: IBankAccountFormViewProps) => {
  return (
    <ModalContainer
      isModalOpen={isModalOpen}
      closeModal={closeModal}
      modalHeader={
        <SectionHeader
          title="Add New Bank Account"
          subtitle="A payout is the transfer of earnings from your SwitchApp to your bank account."
        />
      }
    >
      <form onSubmit={handleSubmit}>
        <HookFormSelect
          name="country"
          label="Country"
          infoIconText="Country where the withdrawal bank account is located"
          showSkeleton={isLoadingSupportedCountries}
          selectItems={[]}
          selectGroupedItems={filterSupportedCountriesFromList(
            swAppSupportedCountries
          )}
          register={register}
          errors={errors}
        />

        <div className="flex-container flex-break--small">
          <div className="flex-box mr-md-5">
            <HookFormSelect
              name="currency"
              label="Currency"
              infoIconText="The currency of the withdrawal bank account eg USD"
              showSkeleton={isLoadingSupportedCurrencies}
              selectItems={swAppSupportedCurrencies.map((cur) => ({
                text: `${cur.currency_name} (${cur.currency_symbol})`,
                value: cur.currency,
              }))}
              register={register}
              errors={errors}
              disabled={!formValues.country}
            />
          </div>
          <div className="flex-box">
            <HookFormSelect
              name="bank_code"
              label="Bank"
              infoIconText="The withdrawal bank"
              showSkeleton={isLoadingBankList}
              selectItems={bankList.map((bnk) => ({
                text: bnk.bank_name,
                value: bnk.bank_code,
              }))}
              register={register}
              errors={errors}
              disabled={!formValues.country || !formValues.currency}
            />
          </div>
        </div>

        <div className="flex-container flex-break--small">
          <div className="flex-box mr-md-5">
            <TextInput
              name="account_number"
              label="Account Number"
              infoIconText="The withdrawal account number"
              type="text"
              register={register}
              errors={errors}
            />

            <div className="flex-container">
              <BankAccountResolver
                accountNumber={formValues.account_number}
                bankCode={formValues.bank_code}
                country={formValues.country}
                currency={formValues.currency}
                isBankAccountResolved={isBankAccountResolved}
                setBankAccountResolved={setBankAccountResolved}
              />
            </div>
          </div>
          <div className={`${formValues.country !== "NG" ? null : "flex-box"}`}>
            {formValues.country !== "NG" ? null : (
              <TextInput
                name="bvn"
                label={`BVN ${
                  formValues.country === "NG" ? "" : " (Optional)"
                }`}
                infoIconText="The Bank Verification Number connected to this account number"
                type="text"
                register={register}
                errors={errors}
              />
            )}
          </div>
        </div>

        <div className="flex-container justify-content--end">
          <HookFormToggle
            name="set_as_primary"
            label="Set As Primary Account Number"
            // labelOff="No"
            labelOff={<>No&nbsp;</>}
            labelOn="Yes"
            size="sm"
            containerClassName="settings-toggle"
            defaultToggled={formValues.set_as_primary}
            onToggle={onToggleSetPrimary}
            infoIconText={
              <>
                Your primary account is where funds are deposited by default
                unless otherwise specified.
                <br />
                NOTE: this will only be effected after account is verified.
              </>
            }
            infoIconAlignment="left"
            register={register}
            errors={errors}
          />
        </div>

        <div className="">
          <strong className="mr-5">
            Account Tags (For Settlements/Payouts):
          </strong>
          {bankAccountTagsFieldArray.map((tag, index) => (
            <div
              className="mr-5 cursor-pointer"
              title="Click to remove tag"
              onClick={() => removeBankAccountTagByIndex(index)}
            >
              <Badge
                text={tag.tag_name}
                hideIndicator
                statusColor="teal"
                className="pl-5 mt-5 bank-account-tag"
              />

              <span className="delete-tag--indicator text-danger font-ctm--bold">
                &times;
              </span>
            </div>
          ))}
        </div>

        <div className="flex-container col">
          <TextInput
            name="formHelperTagName"
            label="Tag Name"
            infoIconText="Tag name displayed for this account number eg School Uniform Acct, Shop Sales Acct"
            type="text"
            register={register}
            errors={errors}
          />
          <TextArea
            name="formHelperTagDescription"
            label="Tag Description (Optional)"
            infoIconText="More info about this tag."
            register={register}
            errors={errors}
          />

          <SwitchAppButton
            kind="success"
            size="sm"
            className="mt-5"
            onClick={appendNewBankAccountTag}
          >
            + Add Tag To List
          </SwitchAppButton>
        </div>

        <div className="flex-container justify-content--end mt-5">
          <div
            id="md_alignment_helper"
            className="flex-box flex-1_5 d-none d-md-flex"
          />
          <div className="flex-box flex-container">
            <SwitchAppButton kind="secondary" onClick={closeModal} size="sm">
              Cancel
            </SwitchAppButton>

            <SubmitButton
              size="sm"
              className="ml-5"
              disabled={!isBankAccountResolved}
            >
              Save
            </SubmitButton>
          </div>
        </div>
      </form>
      <DevTool control={control} /> {/* set up the dev tool */}
    </ModalContainer>
  );
};
